import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { AppLayout } from '../../components/templates/AppLayout/AppLayout';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/Sidebar';
import { PageContent } from '../../components/templates/PageContent';
import { PageHeader } from '../../components/templates/PageHeader';
import { Section } from '../../components/templates/Section';
import { Card } from '../../components/atoms/Card';
import { Stack } from '../../components/atoms/Stack';
import { Inline } from '../../components/atoms/Inline';
import { Grid } from '../../components/atoms/Grid';
import { Badge, type BadgeTone } from '../../components/atoms/Badge';
import { Button } from '../../components/atoms/Button';
import { Icon } from '../../components/atoms/Icon/Icon';
import { Heading, Text } from '../../components/atoms/Typography';
import { Alert } from '../../components/atoms/Alert';
import { LiveRegion } from '../../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../../components/ui/Dialog/Dialog';
import { AiFeedbackControl } from '../../components/aiFeedback/AiFeedbackControl';
import { useTranslation } from '../../i18n/useTranslation';
import { ZeovitModuleGate } from '../../components/zeovit/ZeovitModuleGate';
import { SUPPLY_SYSTEM_NOT_ZEOVIT } from '../../utils/supplySystem';
import { useToast } from '../../stores/toastStore';
import { ApiError } from '../../lib/apiClient';
import {
  useAdoptZeovitRecommendation,
  useZeovitAdoptPreview,
  useZeovitRecommendation,
} from '../../hooks/useZeovitRecommendation';
import {
  ZeovitDosingReviewSection,
  ZeovitObservationsSection,
} from '../../components/zeovit/ZeovitRecommendationReview';
import {
  ZEOVIT_MAX_ITERATIONS,
  type ZeovitAdoptPreview,
  type ZeovitPlanToEnd,
  type ZeovitRecommendation,
  type ZeovitRecommendationStatus,
  type ZeovitRecommendedProduct,
} from '../../services/zeovitRecommendationService';
import './RecommendationDetailPage.css';

/** Status → Badge tone + decorative icon. The Badge text always carries the meaning. */
const STATUS_PRESENTATION: Record<ZeovitRecommendationStatus, { tone: BadgeTone; icon: string }> = {
  pending: { tone: 'info', icon: 'clock' },
  adopted: { tone: 'success', icon: 'check' },
  superseded: { tone: 'neutral', icon: 'history' },
  capped: { tone: 'warning', icon: 'alert-triangle' },
};

const HOURS_PER_DAY = 24;

function formatNumber(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(value);
}

function formatDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

/** "24 h" → "1 day", "36 h" → "36 hours": whole days read better than hours. */
function formatInterval(hours: number, t: TFunction, language: string): string {
  if (hours >= HOURS_PER_DAY && hours % HOURS_PER_DAY === 0) {
    const days = hours / HOURS_PER_DAY;
    return t('zeovit.detail.product.days', { count: days, value: formatNumber(days, language) });
  }
  return t('zeovit.detail.product.hours', { count: hours, value: formatNumber(hours, language) });
}

/**
 * The stored basis of a recommendation (v1.6.2, snapshotMetadata): the dates of the
 * measurements and ICP analysis it used, and whether some values were older than the
 * stale limit. Null for older recommendations without metadata.
 */
function basisLines(recommendation: ZeovitRecommendation, t: TFunction, language: string): string[] | null {
  const metadata = recommendation.snapshotMetadata;
  if (!metadata) return null;
  const lines: string[] = [];
  const measurementDate = metadata.latestMeasurementAt ? formatDate(metadata.latestMeasurementAt, language) : '';
  if (measurementDate) lines.push(t('zeovit.detail.basis.measurements', { date: measurementDate }));
  else if (metadata.measurementOrigin === 'none') lines.push(t('zeovit.detail.basis.noMeasurements'));
  if (metadata.icp) {
    const icpDate = formatDate(`${metadata.icp.reportDate}T00:00:00`, language);
    if (icpDate) lines.push(t('zeovit.detail.basis.icp', { date: icpDate, count: metadata.icp.parameterCount }));
  }
  if (metadata.staleParameters.length > 0) {
    lines.push(t('zeovit.detail.basis.stale', { days: metadata.staleAfterDays }));
  }
  return lines;
}

/** Adopt failures → translated message (never the raw backend text). */
function adoptErrorKey(error: Error | null): string {
  if (error instanceof ApiError && error.status === 409) {
    // The aquarium's supply system is no longer ZeoVit (#1784): the module notice text.
    if (error.code === SUPPLY_SYSTEM_NOT_ZEOVIT) return 'zeovit.unavailable.message';
    // The plans to end changed since the dialog loaded them: re-check and confirm again.
    if (error.code === 'ADOPT_CONFIRMATION_MISMATCH') return 'zeovit.detail.adoptError.plansChanged';
    return error.code === 'DOSING_SCHEDULE_CONFLICT'
      ? 'zeovit.detail.adoptError.conflict'
      : 'zeovit.detail.adoptError.notPending';
  }
  return 'zeovit.detail.adoptError.generic';
}

/** One plan the adoption ends, as a sentence (product, current dose, reason). */
function planToEndText(plan: ZeovitPlanToEnd, t: TFunction, language: string): string {
  const dose =
    plan.doseAmount === null ? plan.doseUnit : `${formatNumber(plan.doseAmount, language)} ${plan.doseUnit}`.trim();
  if (plan.reason === 'replaced') return t('zeovit.detail.adoptDialog.endReplaced', { product: plan.productName, dose });
  return plan.reviewReason
    ? t('zeovit.detail.adoptDialog.endStop', { product: plan.productName, dose, reason: plan.reviewReason })
    : t('zeovit.detail.adoptDialog.endStopNoReason', { product: plan.productName, dose });
}

interface AdoptPreviewListProps {
  preview: ZeovitAdoptPreview | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
}

/**
 * The plans adopting will end (live preview), listed before the user confirms —
 * nothing is ended without this confirmation.
 */
function AdoptPreviewList({ preview, isLoading, isError, isFetching, onRetry }: AdoptPreviewListProps) {
  const { t, i18n } = useTranslation();
  if (isLoading) {
    return (
      <LiveRegion busy>
        <Text color="muted">{t('zeovit.detail.adoptDialog.previewLoading')}</Text>
      </LiveRegion>
    );
  }
  if (isError || !preview) {
    return (
      <Alert
        tone="danger"
        compact
        live="off"
        action={
          <Button variant="secondary" size="sm" onClick={onRetry} isLoading={isFetching}>
            {t('zeovit.detail.adoptDialog.previewRetry')}
          </Button>
        }
      >
        {t('zeovit.detail.adoptDialog.previewError')}
      </Alert>
    );
  }
  if (preview.plansToEnd.length === 0) {
    return <Text color="muted">{t('zeovit.detail.adoptDialog.noEnd')}</Text>;
  }
  return (
    <Stack gap="2" data-testid="zeovit-adopt-plans-to-end">
      <Text weight="medium">{t('zeovit.detail.adoptDialog.endIntro')}</Text>
      <ul className="zeovit-detail__end-list">
        {preview.plansToEnd.map((plan) => (
          <li key={plan.planRef}>
            <Text>{planToEndText(plan, t, i18n.language)}</Text>
          </li>
        ))}
      </ul>
    </Stack>
  );
}

interface ProductCardProps {
  product: ZeovitRecommendedProduct;
  /** The capped (partial) view only lists name and dose. */
  compact?: boolean;
}

function ProductCard({ product, compact = false }: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const dose = `${formatNumber(product.doseAmount, language)} ${product.doseUnit}`;
  const name = t('zeovit.detail.product.name', { productName: product.productName });

  if (compact) {
    return (
      <li>
        <Card data-testid="zeovit-product-card">
          <Stack gap="2">
            <Heading level={3}>{name}</Heading>
            <Text color="muted">{t('zeovit.detail.product.dose', { dose })}</Text>
          </Stack>
        </Card>
      </li>
    );
  }

  // Clamp notes name the value that was reduced: AI suggestion → applied safe value.
  const clampNotes: string[] = [];
  if (product.clamped) {
    if (product.aiSuggestedDoseAmount !== product.doseAmount) {
      clampNotes.push(
        t('zeovit.detail.product.clampNote', {
          preClampValue: `${formatNumber(product.aiSuggestedDoseAmount, language)} ${product.doseUnit}`,
          postClampValue: dose,
        }),
      );
    }
    if (product.aiSuggestedFrequencyHours !== product.frequencyHours) {
      clampNotes.push(
        t('zeovit.detail.product.clampNote', {
          preClampValue: formatInterval(product.aiSuggestedFrequencyHours, t, language),
          postClampValue: formatInterval(product.frequencyHours, t, language),
        }),
      );
    }
  }

  return (
    <li>
      <Card data-testid="zeovit-product-card">
        <Stack gap="2">
          <Inline justify="between" wrap>
            <Heading level={3}>{name}</Heading>
            {product.clamped ? (
              <Badge tone="warning">
                <Icon name="alert-triangle" size="sm" />
                {t('zeovit.detail.clampedBadge')}
              </Badge>
            ) : null}
          </Inline>
          <Text color="muted">{t('zeovit.detail.product.dose', { dose })}</Text>
          <Text color="muted">
            {t('zeovit.detail.product.frequency', {
              frequency: formatInterval(product.frequencyHours, t, language),
            })}
          </Text>
          {product.notes ? (
            <Text>{t('zeovit.detail.product.explanation', { explanation: product.notes })}</Text>
          ) : null}
          {clampNotes.map((note) => (
            <Text key={note} color="muted">
              {note}
            </Text>
          ))}
        </Stack>
      </Card>
    </li>
  );
}

interface ProductsSectionProps {
  products: readonly ZeovitRecommendedProduct[];
  compact?: boolean;
}

function ProductsSection({ products, compact }: ProductsSectionProps) {
  const { t } = useTranslation();
  return (
    <Section title={t('zeovit.detail.productsSection')}>
      <Grid as="ul" minItemWidth="sm" gap="4" className="zeovit-detail__products">
        {products.map((product) => (
          <ProductCard key={product.productId} product={product} compact={compact} />
        ))}
      </Grid>
    </Section>
  );
}

interface FeedbackSectionProps {
  recommendation: ZeovitRecommendation;
}

function FeedbackSection({ recommendation }: FeedbackSectionProps) {
  const { t } = useTranslation();
  return (
    <Section title={t('zeovit.detail.feedbackSection')}>
      <AiFeedbackControl
        outputType="zeovit_dosing_recommendation"
        outputId={recommendation.id}
        aquariumId={recommendation.aquariumId}
        labels={{
          prompt: t('zeovit.detail.feedbackPrompt'),
          up: t('zeovit.detail.feedback.up'),
          down: t('zeovit.detail.feedback.down'),
          commentLabel: t('zeovit.detail.feedback.commentLabel'),
          submit: t('zeovit.detail.feedback.submit'),
        }}
      />
    </Section>
  );
}

function RecommendationDetailPageContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { aquariumId, recommendationId } = useParams<{
    aquariumId: string;
    recommendationId: string;
  }>();

  const { data: recommendation, isLoading, isError, isFetching, refetch } = useZeovitRecommendation(
    aquariumId,
    recommendationId,
  );
  const adoptMutation = useAdoptZeovitRecommendation(aquariumId, recommendationId);

  const [isAdoptDialogOpen, setIsAdoptDialogOpen] = useState(false);
  const adoptPreview = useZeovitAdoptPreview(aquariumId, recommendationId, isAdoptDialogOpen);
  const [focusAdoptArea, setFocusAdoptArea] = useState(false);
  const adoptAreaRef = useRef<HTMLDivElement>(null);

  // After a successful adoption the Adopt button is replaced by the "Adopted" status:
  // move focus to that area once the dialog has closed (its own restore target is gone).
  useEffect(() => {
    if (focusAdoptArea && !isAdoptDialogOpen) {
      adoptAreaRef.current?.focus();
      setFocusAdoptArea(false);
    }
  }, [focusAdoptArea, isAdoptDialogOpen]);

  const openAdoptDialog = () => {
    adoptMutation.reset();
    setIsAdoptDialogOpen(true);
  };

  const closeAdoptDialog = () => {
    if (adoptMutation.isPending) return;
    setIsAdoptDialogOpen(false);
  };

  const confirmAdopt = () => {
    const planRefs = adoptPreview.data?.plansToEnd.map((plan) => plan.planRef) ?? [];
    adoptMutation.mutate(planRefs, {
      onError: (error) => {
        // The plans changed meanwhile: reload the list so the user confirms the current one.
        if (error instanceof ApiError && error.code === 'ADOPT_CONFIRMATION_MISMATCH') void adoptPreview.refetch();
      },
      onSuccess: () => {
        setIsAdoptDialogOpen(false);
        setFocusAdoptArea(true);
        showToast(t('zeovit.detail.adopted'), 'success');
      },
    });
  };

  const goToPeriodicCheck = () => {
    if (!aquariumId || !recommendationId) return;
    navigate(
      `/aquariums/${encodeURIComponent(aquariumId)}/zeovit-recommendations/${encodeURIComponent(recommendationId)}/check`,
    );
  };

  const goToDosingSchedule = () => {
    if (!aquariumId) return;
    navigate(`/aquariums/${encodeURIComponent(aquariumId)}/dosing`);
  };

  // A 409 (no longer pending / schedule conflict) cannot succeed on a retry — except a
  // changed plan list, which the user can confirm again once it is reloaded.
  const adoptRejected =
    adoptMutation.error instanceof ApiError &&
    adoptMutation.error.status === 409 &&
    adoptMutation.error.code !== 'ADOPT_CONFIRMATION_MISMATCH';
  const previewReady = Boolean(adoptPreview.data) && !adoptPreview.isFetching;
  const language = i18n.language;
  const isCapped = recommendation?.status === 'capped';
  const isPending = recommendation?.status === 'pending';
  const canRunCheck = recommendation?.status === 'pending' || recommendation?.status === 'adopted';

  let description: string | undefined;
  if (recommendation) {
    description = isCapped
      ? t('zeovit.detail.cappedLabel')
      : t('zeovit.detail.iterationLabel', {
          number: recommendation.iterationNumber,
          max: ZEOVIT_MAX_ITERATIONS,
        });
  }

  let actions;
  if (recommendation) {
    const status = STATUS_PRESENTATION[recommendation.status];
    actions = (
      <div ref={adoptAreaRef} tabIndex={-1} className="zeovit-detail__adopt-area">
        <Inline gap="2" align="center" wrap>
          <Badge tone={status.tone}>
            <Icon name={status.icon} size="sm" />
            {t(`zeovit.status.${recommendation.status}`)}
          </Badge>
          {isPending ? (
            <Button variant="primary" onClick={openAdoptDialog}>
              {t('zeovit.detail.adopt')}
            </Button>
          ) : null}
        </Inline>
      </div>
    );
  }

  let body;
  if (isLoading) {
    body = (
      <Card>
        <LiveRegion busy>
          <Text color="muted">{t('zeovit.detail.loading')}</Text>
        </LiveRegion>
      </Card>
    );
  } else if (isError || !recommendation) {
    body = (
      <LoadErrorState
        title={t('zeovit.detail.error.loadFailed')}
        onRetry={() => {
          void refetch();
        }}
        retryLabel={t('zeovit.detail.error.retry')}
        retrying={isFetching}
      />
    );
  } else if (isCapped) {
    // Partial state: no further automatic adjustment, so no primary AI action.
    body = (
      <Stack gap="8">
        <Alert tone="warning" live="off">
          <Stack gap="2">
            <Text>{t('zeovit.detail.capped.message')}</Text>
            <Text>{t('zeovit.detail.capped.guidance')}</Text>
            <Inline>
              <Button variant="secondary" onClick={goToDosingSchedule}>
                {t('zeovit.detail.capped.manualReviewLink')}
              </Button>
            </Inline>
          </Stack>
        </Alert>
        {recommendation.recommendedProducts.length > 0 ? (
          <ProductsSection products={recommendation.recommendedProducts} compact />
        ) : null}
        <FeedbackSection recommendation={recommendation} />
      </Stack>
    );
  } else {
    const basedOnDate = formatDate(recommendation.createdAt, language);
    const basis = basisLines(recommendation, t, language);
    body = (
      <Stack gap="8">
        {recommendation.rationale ? (
          <Section title={t('zeovit.detail.rationaleSection')}>
            <Card>
              <Stack gap="3">
                <Text>{t('zeovit.detail.rationaleText', { rationale: recommendation.rationale })}</Text>
                {basedOnDate ? (
                  <Text color="muted">
                    <time dateTime={recommendation.createdAt}>
                      {t('zeovit.detail.basedOn', { date: basedOnDate })}
                    </time>
                  </Text>
                ) : null}
                {basis && basis.length > 0 ? (
                  <ul className="zeovit-detail__basis" data-testid="zeovit-detail-basis">
                    {basis.map((line) => (
                      <li key={line}>
                        <Text color="muted" size="sm">
                          {line}
                        </Text>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Stack>
            </Card>
          </Section>
        ) : null}
        <ZeovitObservationsSection recommendation={recommendation} />
        <ZeovitDosingReviewSection recommendation={recommendation} />
        {recommendation.recommendedProducts.length > 0 ? (
          <ProductsSection products={recommendation.recommendedProducts} />
        ) : null}
        <FeedbackSection recommendation={recommendation} />
        {canRunCheck ? (
          <Section title={t('zeovit.detail.nextSteps')}>
            <Card>
              <Stack gap="2">
                <Text color="muted">{t('zeovit.detail.nextStepsHint')}</Text>
                <Inline>
                  <Button variant="secondary" onClick={goToPeriodicCheck}>
                    {t('zeovit.detail.runCheck')}
                  </Button>
                </Inline>
              </Stack>
            </Card>
          </Section>
        ) : null}
      </Stack>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader title={t('zeovit.detail.title')} description={description} actions={actions} />
          {body}
          <Dialog
            isOpen={isAdoptDialogOpen}
            onClose={closeAdoptDialog}
            title={t('zeovit.detail.adoptDialog.title')}
            size="md"
            data-testid="zeovit-adopt-dialog"
          >
            <Stack gap="3">
              <Text>{t('zeovit.detail.adoptDialog.body')}</Text>
              <AdoptPreviewList
                preview={adoptPreview.data}
                isLoading={adoptPreview.isLoading}
                isError={adoptPreview.isError}
                isFetching={adoptPreview.isFetching}
                onRetry={() => {
                  void adoptPreview.refetch();
                }}
              />
              {adoptMutation.isError ? (
                <Alert tone="danger" compact>
                  {t(adoptErrorKey(adoptMutation.error))}
                </Alert>
              ) : null}
              <Inline gap="2" wrap>
                <Button
                  variant="primary"
                  onClick={confirmAdopt}
                  isLoading={adoptMutation.isPending}
                  disabled={adoptMutation.isPending || adoptRejected || !previewReady}
                >
                  {t('zeovit.detail.adoptDialog.confirm')}
                </Button>
                <Button variant="secondary" onClick={closeAdoptDialog} disabled={adoptMutation.isPending}>
                  {t('zeovit.detail.adoptDialog.cancel')}
                </Button>
              </Inline>
            </Stack>
          </Dialog>
        </PageContent>
      }
    />
  );
}

/**
 * Only for aquariums whose supply system is ZeoVit (#1784): otherwise the page shows
 * the info notice instead (ZeovitModuleGate).
 */
export function RecommendationDetailPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  return (
    <ZeovitModuleGate aquariumId={aquariumId} title={t('zeovit.detail.title')} width="default">
      <RecommendationDetailPageContent />
    </ZeovitModuleGate>
  );
}

export default RecommendationDetailPage;
